<script setup>
import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/vue";
import { logoutService } from "@/Stores/logoutService";
import { useUserStore} from "@/Stores/userStore";
import { useCreditStore } from "@/Stores/creditStore";
import { useUniversalChatStore } from "@/Stores/universalChatStore";
import {useRoute, useRouter} from "vue-router";
import accountApi from "@/Api/account-api";
import LoginModal from "@/Modals/LoginModal.vue";
import RegistrationModal from "@/Modals/RegistrationModal.vue";
import {computed, reactive} from "vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import PermissionConstants from "@/Constants/permission-constants";

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const creditStore = useCreditStore();
const chatStore = useUniversalChatStore();

const data = reactive({
    displayLogin: false,
    displayRegister: false,
    authCredentials: {
        email: '',
        password: '',
    },
})

const isAdminOrModerator = computed(() => {
    return userStore.hasPermission(PermissionConstants.permissions.admin) ||
           userStore.hasPermission(PermissionConstants.permissions.moderator);
})

// Mirrors the order of the primary navigation in MainHeader
const toolsNavigation = [
    {name: 'Compendium', page: '/compendium', current: false, icon: ['fad', 'books']},
    {name: 'Creatures', page: '/creature', current: false, icon: ['far', 'paw-claws']},
    {name: 'Characters', page: '/character', current: false, icon: ['fad', 'users']},
    {name: 'Equipment', page: '/equipment', current: false, icon: ['far', 'sword']},
    {name: 'Maps', page: '/etch/editor', current: false, icon: ['far', 'map']},
    {name: 'Ambience', page: '/ambience', current: false, icon: ['fad', 'mandolin']},
]

const siteNavigation = [
    {name: 'Merchants', page: '/merchant', current: false, icon: ['fad', 'sack']},
    {name: 'Search', page: '/search', current: false, icon: ['far', 'search']},
    {name: 'Plans', page: '/pricing', current: false, icon: ['fad', 'coin-vertical']},
    {name: 'Blog', page: '/blog', current: false, icon: ['fad', 'feather-pointed']},
]

const userNavigation = [
    {name: 'History', page: '/history', current: false, icon: ['far', 'rectangle-history']},
    {name: 'Collections', page: '/collections', current: false, icon: ['far', 'book-sparkles']},
    {name: 'Favorites', page: '/favorites', current: false, icon: ['far', 'heart']},
    {name: 'Etch Projects', page: '/etch/projects', current: false, icon: ['far', 'folders']},
]

const adminNavigation = [
    {name: 'Campaigns', page: '/adventures', current: false, icon: ['far', 'dice-d20']},
    {name: 'Active Campaigns', page: '/campaign-journal', current: false, icon: ['far', 'scroll-old']},
    {name: 'Campaign&nbsp;Studio', page: '/campaign-studio', current: false, icon: ['fad', 'pen-paintbrush']},
    {name: 'Moderate', page: '/moderate', current: false, icon: ['fad', 'shield-check']},
    {name: 'Compose', page: '/compose', current: false, icon: ['fad', 'music']},
    {name: 'Synthesize', page: '/synthesize', current: false, icon: ['fad', 'waveform-lines']},
    {name: 'Style Studio', page: '/style-studio', current: false, icon: ['fad', 'palette']},
]

async function logout() {
    logoutService.logout();
    await router.push('/');
}

function didAuthenticate() {
    data.displayLogin = false;
    data.displayRegister = false;
}

function handleSwitchToRegister(creds) {
    if (creds) {
        data.authCredentials.email = creds.email || '';
        data.authCredentials.password = creds.password || '';
    }
    data.displayLogin = false;
    data.displayRegister = true;
}

function handleSwitchToLogin(creds) {
    if (creds) {
        data.authCredentials.email = creds.email || '';
        data.authCredentials.password = creds.password || '';
    }
    data.displayRegister = false;
    data.displayLogin = true;
}

function routeTo(page) {
    router.push({ name: page })
}

function loginSignupPressed() {
    // Determines if the user should be shown the login or register modal.
    if (userStore.getHasRegistered()) {
        data.displayLogin = true
    } else {
        data.displayRegister = true
    }
}

const currentEnergy = computed(() => {
    if (creditStore.unlimited === true) {
        return "∞"
    }
    return `${creditStore.currentBalance}/${creditStore.maxBalance}`
})

const isLoggedIn = computed(() => {
    return userStore.userId != null;
});

const routeName = computed(() => route.name)

const profileImageUrl = computed(() => {
    return chatStore.currentUser?.portrait_url || '/images/witch.svg';
});

</script>

<template>
    <Menu as="div" class="relative ml-4 flex-shrink-0">
        <LoginModal v-if="data.displayLogin" v-show="data.displayLogin" :initial-email="data.authCredentials.email" :initial-password="data.authCredentials.password" @close="data.displayLogin = false" @register="handleSwitchToRegister" @login-success="didAuthenticate"/>
        <RegistrationModal v-if="data.displayRegister" v-show="data.displayRegister" :initial-email="data.authCredentials.email" :initial-password="data.authCredentials.password" @close="data.displayRegister = false" @login="handleSwitchToLogin" @registration-success="didAuthenticate"/>
        <div v-if="!isLoggedIn">
            <button @click="loginSignupPressed" :class="'w-full text-left block px-4 py-2 mt-1.5 text-sm font-semibold text-white'"> Login / Signup </button>
        </div>
        <div v-else>
            <MenuButton class="flex rounded-full bg-indigo-700 text-sm text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-700">
                <span class="sr-only">Open user menu</span>
                <img class="h-8 w-8 rounded-full object-cover" :src="profileImageUrl" alt="" />
            </MenuButton>
            <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                <MenuItems class="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-md bg-white  shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <!-- Credits display -->
                    <div class="px-4 py-2 border-b border-gray-300 bg-gradient-to-r from-indigo-50 to-purple-50">
                        <div class="flex items-center justify-between">
                            <span class="text-sm text-gray-700 flex items-center">
                                <FontAwesomeIcon :icon="['fad', 'flask']" class="w-4 mr-1" size="lg" style="color: #6b7280"/>
                                Credits
                            </span>
                            <span class="text-sm font-semibold" :class="creditStore.unlimited ? 'text-green-600' : 'text-indigo-600'">
                                {{ currentEnergy }}
                            </span>
                        </div>
                    </div>
                    <div class="py-1 border-b border-gray-300">
                        <MenuItem v-slot="{ active }" class="cursor-pointer">
                            <router-link
                                to="/account"
                                :class="[
                                    '/account' === $route.path ? 'bg-gray-200' : '',
                                    'block px-4 py-2 text-sm text-gray-700',
                                    'hover:bg-gray-100'
                                ]"
                            >
                                <FontAwesomeIcon :icon="['far', 'circle-user']" class="w-4 mr-1" size="lg" style="cursor: pointer; color: #6b7280"/>
                                Account
                            </router-link>
                        </MenuItem>
                    </div>
                    <div class="py-1 border-t border-gray-300">
                        <MenuItem v-for="item in toolsNavigation" :key="item.name" v-slot="{ active }" class="cursor-pointer">
                            <router-link
                                :to="item.page"
                                :class="[
                                    item.page === $route.path ? 'bg-gray-200' : '',
                                    'block px-4 py-2 text-sm text-gray-700',
                                    'hover:bg-gray-100'  // Added hover effect
                                ]"
                            >
                                <FontAwesomeIcon :icon="item.icon" class="w-4 mr-1" size="lg" style="cursor: pointer; color: #6b7280"/>
                                <span v-html="item.name"></span>
                            </router-link>
                        </MenuItem>
                    </div>
                    <div class="py-1 border-t border-gray-300">
                        <MenuItem v-for="item in siteNavigation" :key="item.name" v-slot="{ active }" class="cursor-pointer">
                            <router-link
                                :to="item.page"
                                :class="[
                                    item.page === $route.path ? 'bg-gray-200' : '',
                                    'block px-4 py-2 text-sm text-gray-700',
                                    'hover:bg-gray-100'
                                ]"
                            >
                                <FontAwesomeIcon :icon="item.icon" class="w-4 mr-1"  size="lg" style="cursor: pointer; color: #6b7280"/>
                                <span v-html="item.name"></span>
                            </router-link>
                        </MenuItem>
                    </div>
                    <div class="py-1 border-t border-gray-300">
                    <MenuItem v-for="item in userNavigation" :key="routeName === item.page" v-slot="{ active }" class="cursor-pointer" >
                        <router-link
                            :to="item.page"
                            :class="[
                                item.page === $route.path ? 'bg-gray-200' : '',
                                'block px-4 py-2 text-sm text-gray-700',
                                'hover:bg-gray-100'
                            ]"
                        >
                            <FontAwesomeIcon :icon="item.icon" class="w-4 mr-1"  size="lg" style="cursor: pointer; color: #6b7280"/>
                            <span v-html="item.name"></span>
                        </router-link>
                    </MenuItem>
                    </div>
                    <div v-if="isAdminOrModerator" class="py-1 border-t border-gray-300">
                        <MenuItem v-for="item in adminNavigation" :key="item.page" v-slot="{ active }" class="cursor-pointer">
                            <router-link
                                :to="item.page"
                                :class="[
                                    item.page === $route.path ? 'bg-gray-200' : '',
                                    'block px-4 py-2 text-sm text-gray-700',
                                    'hover:bg-gray-100'
                                ]"
                            >
                                <FontAwesomeIcon :icon="item.icon" class="w-4 mr-1" size="lg" style="cursor: pointer; color: #9333ea"/>
                                <span v-html="item.name"></span>
                            </router-link>
                        </MenuItem>
                    </div>
                    <div class=" py-1 border-t border-gray-300">
                        <MenuItem v-slot="{ active }">
                            <button @click="logout" :class="[active ? 'bg-gray-100' : '', 'w-full text-left flex items-center px-4 py-2 text-sm text-gray-700']">
                                <FontAwesomeIcon :icon="['far', 'right-from-bracket']" class="w-4 mr-1" size="lg" style="cursor: pointer; color: #6b7280"/>
                                Logout
                            </button>
                        </MenuItem>
                    </div>
                </MenuItems>
            </transition>
        </div>
    </Menu>
</template>
